import React, { JSX } from "react";
import { Table, TableColumnsType, Tag } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import Text from "../../components/Text/Text";
import SortFieldCustom from "../../components/SortFieldTable/SortFieldCustom";
import color from "../../resource/color";
import { ShopTierCompanyConfig } from "./config";
import {
  AlignTopTableContainer,
  CountRow,
  kpiTextColorBoolean,
} from "./styled";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { compactAmountFormatter } from "../../utility/Formatter";
import TierTablePagination from "./TierTablePagination";
import { CustomerTierSummaryItem, getCustomerTierSummary } from "../../datasource/CustomerTierProgramDatasource";


interface Props {
  config: ShopTierCompanyConfig;
  search: string;
  zone?: string | null;
  tierFilter: string | null;
  status: string;
  isNavPending: boolean;
  sortField?: string;
  currentYear?: number;
  sortDirection?: "ASC" | "DESC";
  onSortChange: (field: string) => void;
  /** Pagination is owned by the ShopTier page store so it survives leaving the page. */
  page: number;
  take: number;
  onPageChange: (page: number) => void;
  onTakeChange: (take: number) => void;
}

function ShopListTab({
  config,
  search,
  zone,
  tierFilter,
  status,
  isNavPending,
  sortField,
  sortDirection,
  currentYear,
  onSortChange: handleSortChange,
  page,
  take,
  onPageChange,
  onTakeChange,
}: Props): JSX.Element {
  const [company] = useLocalStorage("profile", null);
  const navigate = useNavigate();

  const { data: summary, isLoading } = useQuery(
    ["customer-tier-summary", company?.company, tierFilter, zone, search, status, sortField, sortDirection, page, take, isNavPending],
    () =>
      getCustomerTierSummary(isNavPending ? {
        company: company?.company ?? "",
        tierLevelId: tierFilter ?? undefined,
        customerZone: zone ?? undefined,
        search: search || undefined,
        status: status !== "ALL" ? status : "ALL",
        sortBy: sortField || (company?.company === "ICPL" ? "earnTierRank" : "tierChangedAt"),
        sortOrder: sortDirection || "DESC",
        isUpdateNav : false,
        page,
        take,
      } :{
        company: company?.company ?? "",
        tierLevelId: tierFilter ?? undefined,
        customerZone: zone ?? undefined,
        search: search || undefined,
        status: status !== "ALL" ? status : "ALL",
        sortBy: sortField || (company?.company === "ICPL" ? "earnTierRank" : "tierChangedAt"),
        sortOrder: sortDirection || "DESC",
        page,
        take,
      } ),
    { enabled: !!company?.company, refetchOnWindowFocus: false, keepPreviousData: true },
  );

  const shops: CustomerTierSummaryItem[] = summary?.responseData?.data || [];
  const total: number = summary?.responseData?.count || 0;

  const formatThaiDate = (value?: string | null): string => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear() + 543;
    return `${day}/${month}/${year}`;
  };

  const formatAccruedTotal = compactAmountFormatter;

  const columns: TableColumnsType<CustomerTierSummaryItem> = [
    {
      title: (
        <SortFieldCustom
          title="รหัสร้านค้า"
          sortDirection={sortField === "customerNo" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("customerNo")}
        />
      ),
      dataIndex: "customerNo",
      key: "customerNo",
      render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>,
    },
    {
      title: (
        <SortFieldCustom
          title="ร้านค้า"
          sortDirection={sortField === "customerName" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("customerName")}
        />
      ),
      dataIndex: "customerName",
      key: "customerName",
      render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>,
    },
    { title: "จังหวัด", dataIndex: "customerProvince", key: "customerProvince",render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>, },
    { title: "เขต", dataIndex: "customerZone", key: "customerZone", render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>, },
    {
      title: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <SortFieldCustom
            title="ระดับปัจจุบัน"
            sortDirection={sortField === "currentTierRank" ? sortDirection : undefined}
            onSortChange={() => handleSortChange("currentTierRank")}
          />
          <Text fontSize={12} fontWeight={400} color="Text3">
            ปี {currentYear}
          </Text>
        </div>
      ),
      dataIndex: "currentTierName",
      key: "currentTierName",
      render: (tier: string | null, record: CustomerTierSummaryItem) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          {tier ? (
            <Tag style={{ borderRadius: "16px" }} color={record.currentTierColor!}>
              <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color={kpiTextColorBoolean(record.currentTierColor) ? "Text4" : "white"}>
                {tier}
              </Text>
            </Tag>
          ) : (
            <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">-</Text>
          )}
          {record.isPendingReview && (
            <Text fontFamily="Sarabun" fontSize={12} fontWeight={500} color="warning">
              รอพิจารณาปรับระดับ
            </Text>
          )}
        </div>
      ),
    },
    ...(company?.company === "ICPL"
      ? [
          {
            title: (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <SortFieldCustom
                  title="ระดับคาดการณ์"
                  sortDirection={sortField === "earnTierRank" ? sortDirection : undefined}
                  onSortChange={() => handleSortChange("earnTierRank")}
                />
                <Text fontSize={12} fontWeight={400} color="Text3">
                  ประมาณการจากยอดปีนี้
                </Text>
              </div>
            ),
            dataIndex: "earnedTierName",
            key: "earnedTierName",
            render: (tier: string | null, record: CustomerTierSummaryItem) => (
              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                {tier ? (
                  <Tag style={{ borderRadius: "16px" }} color={record.earnedTierColor!}>
                    <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color={kpiTextColorBoolean(record.earnedTierColor) ? "Text4" : "white"}>
                      {tier}
                    </Text>
                  </Tag>
                ) : (
                  <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">-</Text>
                )}
              </div>
            ),
          } as TableColumnsType<CustomerTierSummaryItem>[number],
        ]
      : []),
    {
      title: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <SortFieldCustom
            title="ยอดสะสม"
            sortDirection={sortField === "accruedTotal" ? sortDirection : undefined}
            onSortChange={() => handleSortChange("accruedTotal")}
          />
          <Text fontSize={12} fontWeight={400} color="Text3">
            {config.volumeUnit} ปี {currentYear}
          </Text>
        </div>
      ),
      dataIndex: "accruedTotal",
      key: "accruedTotal",
      align: "right",
      render: (tier: number | null) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{formatAccruedTotal(company?.company ?? "", tier)}</Text>,
    },
    {
      title: (
        <SortFieldCustom
          title="เปลี่ยนระดับล่าสุด"
          sortDirection={sortField === "tierChangedAt" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("tierChangedAt")}
        />
      ),
      dataIndex: "tierChangedAt",
      key: "tierChangedAt",
      render: (tier: string | null) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{formatThaiDate(tier)}</Text>,
    },
    {
      title: "ประวัติ",
      dataIndex: "historyCount",
      key: "historyCount",
      render: (tier: number | null) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">
            {tier} รายการ
          </Text>
          <RightOutlined style={{ color: color.Text3, fontSize: 12 }} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <CountRow>{total} รายการ</CountRow>
      <AlignTopTableContainer>
        <Table
          rowKey="customerTierId"
          dataSource={shops}
          columns={columns}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          pagination={false}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => navigate(`/ShopTier/detail/${record.customerCompanyId}`),
          })}
        />
      </AlignTopTableContainer>
      <TierTablePagination
        current={page}
        pageSize={take}
        total={total}
        onChange={onPageChange}
        onPageSizeChange={onTakeChange}
      />
    </div>
  );
}

export default ShopListTab;
